import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllAccommodationsByProject } from '../../services/api/implementation/impl';
import Button from '../../../shared/components/ui/Button';
import { useParams } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';

export const UpdateExtraCharges = ({ extraCharge, updateSuccess, onClose }) => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();

  const [typeName, setTypeName] = useState('');
  const [description, setDescription] = useState('');
  const [rateValue, setRateValue] = useState('');
  const [accommodationIds, setAccommodationIds] = useState([]);
  const [isMandatory, setIsMandatory] = useState(true);
  const [gstPercentage, setGstPercentage] = useState('');
  const [removingIds, setRemovingIds] = useState({});

  const [accommodations, setAccommodations] = useState([]);

  const rateType = extraCharge?.mode || 'accommodation'; // read-only: 'accommodation' | 'sqft' | 'sqmt'

  const { data: accommodationData } = useQuery({
    queryKey: ['accommodations', projectId],
    queryFn: () => getAllAccommodationsByProject(projectId),
    enabled: !!projectId
  });

  useEffect(() => {
    mixpanel.track('Update Extra Charges Viewed', {
      user_id: userId,
      extra_charge_id: extraCharge?.id,
      timestamp: new Date().toISOString()
    });
  }, [userId, extraCharge?.id]);

  useEffect(() => {
    if (accommodationData) {
      const options = accommodationData?.map((acc) => ({
        value: acc.id.toString(),
        label: acc.name
      }));
      setAccommodations(options);
    }
  }, [accommodationData]);

  useEffect(() => {
    if (!extraCharge?.id) return;
    const gstVal = extraCharge.gst;
    const gstStr =
      gstVal != null && gstVal !== '' ? Number(gstVal).toFixed(2) : '';
    setTypeName(extraCharge.name || '');
    setDescription(extraCharge.description || '');
    setRateValue(extraCharge.price != null ? String(extraCharge.price) : '');
    setGstPercentage(gstStr);
    setIsMandatory(extraCharge.isMandatory !== false);
    if (extraCharge.accomodations?.length > 0) {
      setAccommodationIds(
        extraCharge.accomodations.map((acc) => String(acc.accomodation_id))
      );
    } else {
      setAccommodationIds([]);
    }
  }, [extraCharge?.id]);

  const handleAccommodationToggle = (accId) => {
    setAccommodationIds((prev) =>
      prev.includes(accId) ? prev.filter((id) => id !== accId) : [...prev, accId]
    );
  };

  const handleRateValueInput = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) value = parts[0] + '.' + parts.slice(1).join('');
    setRateValue(value);
  };

  const handleGstInput = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');
    if (parts[1] && parts[1].length > 2) value = parts[0] + '.' + parts[1].slice(0, 2);
    setGstPercentage(value);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (accommodationIds.length === 0) {
      toast.error('Please select at least one accommodation');
      return;
    }
    if (!gstPercentage.trim()) {
      toast.error('Please enter GST percentage');
      return;
    }
    mixpanel.track('Extra Charge Update Submitted', {
      user_id: userId,
      extra_charge_id: extraCharge.id,
      timestamp: new Date().toISOString()
    });
    const payload = {
      name: typeName.trim(),
      description: description.trim(),
      price: rateValue !== '' ? parseFloat(rateValue) : 0,
      gst: parseFloat(Number(gstPercentage).toFixed(2)),
      isMandatory: Boolean(isMandatory),
      mode: rateType,
      accommodation_ids: accommodationIds
    };
    updateSuccess({ id: extraCharge.id, payload });
  };

  const getRateTypeLabel = () => {
    if (rateType === 'accommodation') return 'Accommodation Based';
    if (rateType === 'sqft') return 'Rate per sqft';
    if (rateType === 'sqmt') return 'Rate per sqmt';
    return rateType;
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6">
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">
              Extra Charges Type Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={typeName}
              onChange={(e) => setTypeName(e.target.value)}
              placeholder="Enter here"
              className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">
              Extra Charges Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter here"
              className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue outline-none"
              required
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-600 mb-2">
            Extra Charges Rate <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            disabled
            value={getRateTypeLabel()}
            className="p-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 w-full max-w-[280px]"
          />
        </div>

        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-600 mb-1.5">
            Enter Here {rateType !== 'accommodation' && `(${rateType})`} <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-2 max-w-[200px]">
            <input
              type="text"
              value={rateValue}
              onChange={handleRateValueInput}
              placeholder="0"
              className="flex-1 p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue outline-none"
              required
            />
          </div>
        </div>
      </div>

      {/* Associated to Accommodation */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Associated to Accommodation <span className="text-red-500">*</span>
        </h4>
        <div className="flex flex-wrap gap-4">
          {accommodations.map((acc) => (
            <label
              key={acc.value}
              className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md hover:bg-gray-50">
              <input
                type="checkbox"
                checked={accommodationIds.includes(acc.value)}
                onChange={() => handleAccommodationToggle(acc.value)}
                className="h-4 w-4 accent-customBlue focus:ring-customBlue border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">{acc.label}</span>
            </label>
          ))}
          {accommodations.length === 0 && (
            <p className="text-sm text-gray-500">No accommodations found for this project.</p>
          )}
        </div>
        {accommodationIds.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {accommodationIds.map((selectedId) => {
              const selectedAcc = accommodations.find((acc) => acc.value === selectedId);
              return selectedAcc ? (
                <div
                  key={selectedId}
                  className="flex items-center gap-2 px-2 py-1 bg-customBlue/10 text-customBlue rounded-full text-xs">
                  <span>{selectedAcc.label}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setRemovingIds((prev) => ({ ...prev, [selectedId]: true }));
                      setTimeout(() => {
                        handleAccommodationToggle(selectedId);
                        setRemovingIds((prev) => ({ ...prev, [selectedId]: false }));
                      }, 150);
                    }}
                    className={`text-customBlue hover:text-customBlue hover:bg-customBlue/20 rounded-full w-5 h-5 flex items-center justify-center text-sm font-bold transition-all duration-200 ${
                      removingIds[selectedId] ? 'bg-red-200 text-red-600 scale-110' : ''
                    }`}
                    title="Remove accommodation"
                    disabled={removingIds[selectedId]}>
                    {removingIds[selectedId] ? '✓' : '×'}
                  </button>
                </div>
              ) : null;
            })}
          </div>
        )}
      </div>

      {/* Choose: Mandatory / Optional */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Choose <span className="text-red-500">*</span></h4>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isMandatory === true}
              onChange={() => setIsMandatory(true)}
              className="h-4 w-4 accent-customBlue focus:ring-customBlue border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Mandatory</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isMandatory === false}
              onChange={() => setIsMandatory(false)}
              className="h-4 w-4 accent-customBlue focus:ring-customBlue border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Optional</span>
          </label>
        </div>
      </div>

      {/* Add GST Percentage (footer area) */}
      <div className="pt-2 border-t border-gray-200">
        <label className="block text-sm font-medium text-gray-600 mb-1.5">
          Add GST Percentage <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2 max-w-[160px]">
          <input
            type="text"
            value={gstPercentage}
            onChange={handleGstInput}
            placeholder="0.00"
            className="flex-1 p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue outline-none"
            required
          />
          <span className="text-gray-500 font-medium">%</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end items-center gap-3 pt-2">
        <Button type="button" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button type="submit">Update</Button>
      </div>
    </form>
  );
};
